import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import * as AuthActions from './auth.actions';
import { SharedLibraryService, ConfirmResetService } from 'projects/petszel-shared/src/public-api';
import { AuthenticationGuardService } from '../../service/authentication-guard.service';
import { AuthService } from 'projects/petszel-shared/src/lib/services/auth.service';

interface LoginResponse {
  isSuccess: boolean;
  token: string;
  expiration: string;
}

interface DecodedToken {
  'petszel-credentialtype': string | string[];
  'petszel-userid': string;
  email: string;
  [key: string]: any;
}

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ username, password, applicationId }) => {
        const url = this.sharedLibraryService.getSharedLibraryConfig().apiUrl + 'Authorize';
        const data = { username, password, applicationId, credentialType: '' };

        return this.http.post<LoginResponse>(url, data).pipe(
          map((response) => {
            if (
              response.isSuccess &&
              response.token &&
              new Date(response.expiration) > new Date()
            ) {
              try {
                const decodedToken = jwtDecode<DecodedToken>(response.token);
                
                let credentials = decodedToken['petszel-credentialtype'];
                if (!Array.isArray(credentials)) {
                  credentials = [credentials];
                }

                const userId = decodedToken['petszel-userid'] || '';
                const userEmail = decodedToken['email'] || username;

                // For staff, only allow PetszelStaff and PetszelAdmin roles
                const allowedRoles = ['PetszelStaff', 'PetszelAdmin'];
                const hasAllowedRole = credentials.some(role => allowedRoles.includes(role));

                if (hasAllowedRole) {
                  this.logger.log('User login successful');
                  return AuthActions.loginSuccess({
                    token: response.token,
                    userId,
                    userEmail,
                    userRoles: credentials as string[]
                  });
                } else {
                  return AuthActions.loginFailure({ error: 'Invalid credentials' });
                }
              } catch (error) {
                return AuthActions.loginFailure({ error: 'Invalid token' });
              }
            } else {
              return AuthActions.loginFailure({ error: 'Invalid credentials' });
            }
          }),
          catchError((error) => {
            this.logger.error(error, 'Login error occurred');
            
            if (error.status === 400 || error.status === 401) {
              return of(AuthActions.loginFailure({ error: 'Invalid credentials' }));
            } else {
              return of(AuthActions.loginFailure({ error: 'Network error' }));
            }
          })
        );
      })
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, userRoles }) => {
          // Persist to localStorage for session management
          localStorage.setItem('token', token);
          
          // Update legacy AuthService for backward compatibility
          this.authService.setLogInStatus(true);

          // Redirect using existing auth guard for staff
          // Find first allowed role (PetszelAdmin takes priority over PetszelStaff)
          const matchedRole = userRoles.find(role => role === 'PetszelAdmin') || 
                             userRoles.find(role => role === 'PetszelStaff');
          if (matchedRole) {
            this.authGuard.redirectCallback(matchedRole);
          }
        })
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(() => {
          localStorage.removeItem('token');
          this.authService.setLogInStatus(false);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        localStorage.removeItem('token');
        this.authService.setLogInStatus(false);
        this.router.navigate(['/']);
      }),
      map(() => AuthActions.logoutSuccess())
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      switchMap(({ email }) => {
        return this.confirmResetService.postResetPassword({ username: email }).pipe(
          map(() => {
            this.logger.log('Reset password email sent successfully');
            return AuthActions.resetPasswordSuccess();
          }),
          catchError((error) => {
            this.logger.error(error, 'Reset password error occurred');
            return of(AuthActions.resetPasswordFailure({ 
              error: 'Unable to send reset email. Please try again shortly.' 
            }));
          })
        );
      })
    )
  );

  resetPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resetPasswordSuccess)
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private router: Router,
    private logger: NGXLogger,
    private sharedLibraryService: SharedLibraryService,
    private authGuard: AuthenticationGuardService,
    private authService: AuthService,
    private confirmResetService: ConfirmResetService
  ) {}
}

